import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  LabelPill,
  Number as NumberUI,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import {
  formatLocalDateComponents,
  NotFoundError,
  neverFunc,
} from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactNode, Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import type { AdminAssetBalanceValidationRowFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/asset-balance-validations_/$fileImportId",
)({
  head: () => opsHead("Balance Validation"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminAssetBalanceValidationRow on AssetBalanceValidation {
    id
    status
    validatedAt
    unitBalance
    fileUnitBalance
    fileAmount {
      amount
      currency
    }
    ledgerAmount {
      amount
      currency
    }
    difference {
      amount
      currency
    }
    unitPrice {
      price
      targetUnit
      at
    }
    asset {
      id
      name
      friendlyIdentifier
      businessEntity {
        id
        name
        organisation {
          id
          name
        }
      }
      ... on FixedIncomeFundAsset {
        product {
          id
          name
        }
      }
    }
  }

  fragment AdminAssetBalanceValidationFileDetails on AssetBalanceValidationImportFile {
    id
    fileName
    uploadedAt
    effectiveAt
    status
    downloadUrl
    assetBalanceValidations {
      ...AdminAssetBalanceValidationRow
    }
  }
`);

function View() {
  const params = useParams({ strict: false });
  if (!params.fileImportId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminAssetBalanceValidationFile($id: ID!) {
        node(id: $id) {
          id
          ... on AssetBalanceValidationImportFile {
            ...AdminAssetBalanceValidationFileDetails
          }
        }
      }
    `),
    { variables: { id: params.fileImportId } },
  );

  const [validateFile, { loading: validating }] = useMutation(
    graphql(`
      mutation RevalidateAssetBalanceValidationFile($input: ValidateAssetBalanceFileInput!) {
        validateAssetBalanceFile(input: $input) {
          importFile {
            ...AdminAssetBalanceValidationFileDetails
          }
        }
      }
    `),
  );

  const file = data.node;
  if (!file || file.__typename !== "AssetBalanceValidationImportFile") {
    throw new NotFoundError();
  }

  useOpsTitle(`Balance validation "${file.fileName}"`);

  const validations = [...(file.assetBalanceValidations ?? [])].sort((a, b) => {
    const byDifference = b.difference.amount
      .abs()
      .comparedTo(a.difference.amount.abs());
    if (byDifference !== 0 || a.status === b.status) {
      return byDifference;
    }
    return a.status === "MISMATCHED" ? -1 : 1;
  });
  const mismatchedCount = validations.filter(
    (v) => v.status === "MISMATCHED",
  ).length;

  let statusPill: ReactNode;
  switch (file.status) {
    case "MATCHED":
      statusPill = <LabelPill variant="success" label="Matched" />;
      break;
    case "MISMATCHED":
      statusPill = <LabelPill variant="error" label="Mismatched" />;
      break;
    case null:
    case undefined:
      statusPill = <LabelPill variant="neutral" label="Not validated" />;
      break;
    default:
      neverFunc(file.status);
  }

  return (
    <AdminPageContainer fullWidth testId="admin-asset-balance-validation">
      <AdminPageHeader
        title={file.fileName}
        subtitle={
          <span className="flex items-center gap-2">
            {statusPill}
            {`${validations.length - mismatchedCount} matched, ${mismatchedCount} mismatched · ledger effective at ${file.effectiveAt ? formatLocalDateComponents(file.effectiveAt).datetime : "-"} · uploaded ${formatLocalDateComponents(file.uploadedAt).datetime}`}
          </span>
        }
        buttons={
          <div className="flex gap-2">
            {file.downloadUrl && (
              <Button variant="outline" asChild>
                <a href={file.downloadUrl}>Download file</a>
              </Button>
            )}
            <Button
              variant="primary"
              loading={validating}
              onClick={async () => {
                try {
                  await validateFile({
                    variables: { input: { importFileId: file.id } },
                  });
                  toast({
                    title: "Revalidated",
                    kind: "success",
                    autoHide: true,
                  });
                } catch (error) {
                  toast({
                    title: "Error revalidating file",
                    description: String(error),
                    kind: "error",
                    autoHide: false,
                  });
                }
              }}
            >
              Revalidate
            </Button>
          </div>
        }
      />

      <DataTable
        data={validations}
        columns={columns}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        linkTo={(row) => `/assets/${row.original.asset.id}`}
      />
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminAssetBalanceValidationRowFragment>[] = [
  {
    id: "entity",
    accessorFn: (row) => row.asset.businessEntity?.organisation?.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => (
      <BusinessEntityColumnCell
        businessEntity={row.original.asset.businessEntity}
      />
    ),
  },
  {
    id: "fund",
    accessorFn: (row) =>
      row.asset.__typename === "FixedIncomeFundAsset"
        ? row.asset.product?.name
        : undefined,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Fund" />
    ),
    cell: ({ row }) => (
      <Link to="/assets/$assetId" params={{ assetId: row.original.asset.id }}>
        {row.original.asset.__typename === "FixedIncomeFundAsset"
          ? row.original.asset.product?.name
          : row.original.asset.name}
      </Link>
    ),
  },
  {
    id: "accountNumber",
    accessorFn: (row) => row.asset.friendlyIdentifier,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    cell: ({ row }) => (
      <span className="font-mono">
        {row.original.asset.friendlyIdentifier ?? "-"}
      </span>
    ),
  },
  {
    id: "unitBalance",
    accessorFn: (row) => row.unitBalance.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Ledger units"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <NumberUI
          value={row.original.unitBalance}
          format={NumberUI.format.decimal({ digits: 4 })}
        />
      </div>
    ),
  },
  {
    id: "fileUnitBalance",
    accessorFn: (row) => row.fileUnitBalance?.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="File units" align="right" />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        {row.original.fileUnitBalance ? (
          <NumberUI
            value={row.original.fileUnitBalance}
            format={NumberUI.format.decimal({ digits: 4 })}
          />
        ) : (
          "-"
        )}
      </div>
    ),
  },
  {
    id: "unitDifference",
    accessorFn: (row) => {
      if (row.fileUnitBalance) {
        return row.fileUnitBalance.minus(row.unitBalance).toNumber();
      }
      return row.difference.amount.toNumber();
    },
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Unit difference"
        align="right"
      />
    ),
    cell: ({ row }) => {
      const { fileUnitBalance, unitBalance } = row.original;
      if (!fileUnitBalance) {
        return "-";
      }
      return (
        <div className="text-right">
          <NumberUI
            value={fileUnitBalance.minus(unitBalance)}
            format={NumberUI.format.decimal({ digits: 4 })}
          />
        </div>
      );
    },
  },
  {
    id: "unitPrice",
    accessorFn: (row) => row.unitPrice.price.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Unit price" align="right" />
    ),
    cell: ({ row }) => (
      <div className="flex flex-row items-center justify-end gap-1">
        <Txt>
          <NumberUI
            value={row.original.unitPrice.price}
            format={NumberUI.format.decimal({ digits: 4 })}
          />{" "}
          {row.original.unitPrice.targetUnit}
          {" at "}
        </Txt>
        <DateUI
          date={row.original.unitPrice.at}
          variant="DAY_MONTH_SHORT_TIME"
        />
      </div>
    ),
  },
  {
    id: "ledgerAmount",
    accessorFn: (row) => row.ledgerAmount.amount.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Ledger balance"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI {...row.original.ledgerAmount} showSymbol={false} />
      </div>
    ),
  },
  {
    id: "fileAmount",
    accessorFn: (row) => row.fileAmount.amount.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="File balance"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI {...row.original.fileAmount} showSymbol={false} />
      </div>
    ),
  },
  {
    id: "difference",
    accessorFn: (row) => {
      if (row.fileUnitBalance) {
        return row.fileUnitBalance.minus(row.unitBalance).toNumber();
      }
      return row.difference.amount.toNumber();
    },
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Currency difference"
        align="right"
      />
    ),
    cell: ({ row }) => {
      const { difference } = row.original;
      return (
        <div className="text-right">
          <CurrencyAmountUI {...difference} showSymbol={false} />
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <LabelPill
        variant={row.original.status === "MATCHED" ? "success" : "error"}
        label={row.original.status === "MATCHED" ? "Matched" : "Mismatched"}
      />
    ),
  },
];
